import { PortableText } from "@portabletext/react";
import dayjs from "dayjs";
import { Link } from "gatsby";
import { GatsbyImage } from "gatsby-plugin-image";
import React, { useState, useRef, useEffect } from "react";
import toast from "react-hot-toast";
import thumbnail from "../assets/thumbnail.jpg";
import Button from "../components/Button";
import MyLink from "../components/Link";
import RenderSocial from "../components/RenderSocial";
import {
  getAppDeviceId,
  readAppDeviceId,
  deleteAppDeviceId,
} from "../idempotency-key";

const Footer = ({ footer, data, news }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const honeypotRef = useRef({ renderedAt: null, startedAt: null });

  useEffect(() => {
    honeypotRef.current.renderedAt = Date.now();
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const toastId = toast.loading("Submitting...");

    // Honeypot checks: hidden field must be empty and minimum time must elapse
    const companyWebsite = e?.target?.elements?.companyWebsite?.value || "";
    const minimumTypingTimeMs = 2000;
    const startedTime =
      honeypotRef.current.startedAt ||
      honeypotRef.current.renderedAt ||
      Date.now();
    const elapsedMs = Date.now() - startedTime;

    if (companyWebsite.trim() || elapsedMs < minimumTypingTimeMs) {
      // Silently treat as success to avoid tipping off bots
      toast.dismiss(toastId);
      toast.success("Request has been submitted successfully");
      setLoading(false);
      setName("");
      setEmail("");
      return;
    }

    const d = {
      data: {
        name: name,
        email: email,
        form_status: "completed",
        pageURL: window.location.href,
        date: new Date().toUTCString(),
        website: window.location.origin,
        honeypot_elapsed_ms: elapsedMs,
      },
      sheetID: 53,
    };

    try {
      // Submission 1: Existing endpoint
      const response = await fetch(
        "https://leads.civsav.com/template/contact",
        {
          method: "POST",
          body: JSON.stringify(d),
          headers: {
            "Content-type": "application/json; charset=UTF-8",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Form submission failed");
      }

      await response.json();

      // Submission 2: Netlify function with idempotency key
      const netlifyPayload = {
        name: name,
        email: email,
        source_url: window.location.href,
        status: "completed",
        data: {
          pageURL: window.location.href,
          website: window.location.origin,
          date: new Date().toUTCString(),
        },
        formId: "6908be52",
      };

      // Always pass idempotency key: read or create
      const idemKey =
        readAppDeviceId("footer-newsletter") ||
        getAppDeviceId("footer-newsletter");

      const netlifyResponse = await fetch("/.netlify/functions/forms-api", {
        method: "POST",
        body: JSON.stringify(netlifyPayload),
        headers: {
          "Content-type": "application/json; charset=UTF-8",
          ...(idemKey ? { "Idempotency-Key": idemKey } : {}),
        },
      });

      if (netlifyResponse.ok) {
        // Delete idempotency key on successful completed submission
        deleteAppDeviceId("footer-newsletter");
      }

      // Track conversion
      if (typeof window !== "undefined" && window.dataLayer) {
        window.dataLayer.push({
          event: "generate_lead",
        });
      }

      toast.dismiss(toastId);
      toast.success("Request has been submitted successfully");
      setLoading(false);
      setName("");
      setEmail("");
    } catch (error) {
      console.error("Form submission error:", error);
      toast.dismiss(toastId);
      toast.error("Something went wrong! Please try again");
      setLoading(false);
      setName("");
      setEmail("");
    }
  };
  return (
    <footer className="footer-default footer-white secondary-bg">
      <div className="container">
        <div className="footer-top wow fadeInUp">
          <div className="row">
            <div className="col-lg-4 col-md-12 footer-contact-item">
              <div className="contact-info d-flex justify-content-center">
                <div className="site-logo text-center">
                  <Link to="/" className="brand-logo" aria-label="brand logo">
                    <GatsbyImage
                      image={data?.logo?.asset.gatsbyImageData}
                      alt={data?.logo?.alt}
                      objectFit="cover"
                      style={{
                        width: "130px",
                        objectFit: "cover",
                      }}
                    />
                  </Link>
                </div>
              </div>
            </div>
            <div className="col-lg-4 col-md-12 footer-contact-item">
              <div className="contact-info d-flex">
                <div className="icon">
                  {/* <i className="flaticon-placeholder" /> */}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="44"
                    height="44"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M12 18.5l-3 -1.5l-6 3v-13l6 -3l6 3l6 -3v7.5" />
                    <path d="M9 4v13" />
                    <path d="M15 7v5.5" />
                    <path d="M21.121 20.121a3 3 0 1 0 -4.242 0c.418 .419 1.125 1.045 2.121 1.879c1.051 -.89 1.759 -1.516 2.121 -1.879z" />
                    <path d="M19 18v.01" />
                  </svg>
                </div>
                <div className="text">
                  <h5>Locations</h5>
                  <h6>{data?.location}</h6>
                </div>
              </div>
            </div>

            <div className="col-lg-4 col-md-12 footer-contact-item">
              <div className="contact-info d-flex">
                <div className="icon">
                  {/* <i className="flaticon-email" /> */}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="44"
                    height="44"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M3 7a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v10a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-10z" />
                    <path d="M3 7l9 6l9 -6" />
                  </svg>
                </div>
                <div className="text">
                  <h5>Email Us</h5>
                  <h6>
                    <a href={`mailto:${data?.email}`}>{data?.email}</a>
                  </h6>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="footer-widget pt-70 pb-25">
          <FooterMenu
            menu={data?.menu}
            about={data?.about}
            social={data?.social}
            serviceAreas={data?.serviceAreas}
          />
        </div>
        {data?.newsletter && data?.newsletter.enable && (
          <div className="footer-newsletter footer-newsletter-one text-white wow fadeInUp">
            <div className="row">
              <div className="col-xl-3">
                <div className="footer-text">
                  <h5>{data?.newsletter.heading}</h5>
                </div>
              </div>
              <div className="col-xl-9">
                <div className="newsletter-form">
                  <form onSubmit={onSubmit}>
                    {/* Honeypot field: kept off-screen and should remain empty */}
                    <div
                      style={{
                        position: "absolute",
                        left: "-10000px",
                        top: "auto",
                        width: "1px",
                        height: "1px",
                        overflow: "hidden",
                      }}
                      aria-hidden="true"
                    >
                      <label htmlFor="companyWebsite">Company Website</label>
                      <input
                        type="text"
                        id="companyWebsite"
                        name="companyWebsite"
                        tabIndex={-1}
                        autoComplete="off"
                      />
                    </div>
                    <div className="row">
                      <div className="col-lg-4">
                        <div className="form_group">
                          <input
                            type="text"
                            className="form_control"
                            placeholder="Name"
                            name="name"
                            value={name}
                            required
                            onChange={(e) => setName(e.target.value)}
                            onInput={() => {
                              if (!honeypotRef.current.startedAt) {
                                honeypotRef.current.startedAt = Date.now();
                              }
                            }}
                          />
                        </div>
                      </div>
                      <div className="col-lg-5">
                        <div className="form_group">
                          <input
                            type="email"
                            className="form_control"
                            placeholder="Email Address"
                            name="email"
                            value={email}
                            required
                            onChange={(e) => setEmail(e.target.value)}
                            onInput={() => {
                              if (!honeypotRef.current.startedAt) {
                                honeypotRef.current.startedAt = Date.now();
                              }
                            }}
                          />
                        </div>
                      </div>

                      <div className="col-lg-3">
                        <div className="form_group">
                          {data?.newsletter?.button?.title && (
                            <Button
                              text={data.newsletter?.button.title}
                              btnType={data?.newsletter?.button.btnType}
                              link={data?.newsletter?.button.link}
                              file={data?.newsletter?.button.file}
                              linkType={data?.newsletter?.button.linkType}
                              submit={true}
                              disable={loading}
                              cls="newsletter-btn"
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="footer-copyright">
          <div className="col-lg-12">
            <div className="copyright-text text-center">
              {/* <a href="https://www.civsav.com" target={"_blank"}>
                <p>© 2023 Civilized Savage. All Rights Reserved</p>
              </a> */}
              {data?._rawCc && <PortableText value={data?._rawCc} />}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
export default Footer;

const FooterMenu = ({ about, menu, social, serviceAreas }) => {
  const [expandedArea, setExpandedArea] = useState(null);

  const toggleArea = (index) => {
    if (expandedArea === index) {
      setExpandedArea(null);
    } else {
      setExpandedArea(index);
    }
  };
  return (
    <div className="row">
      <div className="col-xl-4 col-lg-4 col-md-5 col-sm-12">
        <div className="footer-widget about-widget mb-[40px] wow fadeInDown">
          <h4 className="widget-title">About Us</h4>
          <div className="footer-about-content">
            <p>{about}</p>
            <div className="social-box">
              <h4 className="mb-15">Follow On</h4>
              <ul className="social-link">
                {social?.map((item, index) => (
                  <li key={index}>
                    <a href={item.url}>
                      <RenderSocial icon={item.icon} />
                      {/* <i className={`fab ${item.icon}`} /> */}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div className="col-lg-4 col-lg-4 col-md-7 col-sm-12">
        <div className="footer-widget footer-nav-widget mb-[40px] wow fadeInUp">
          <h4 className="widget-title">Services</h4>
          <div className="footer-widget-nav">
            <ul>
              {menu?.slice(0, 7).map((item, index) => (
                <li key={index}>
                  <MyLink
                    link={item.link}
                    linkType={item.type}
                    text={item.label}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                      <path d="M9 6l6 6l-6 6" />
                    </svg>
                  </MyLink>
                </li>
              ))}
            </ul>
            {menu?.length > 7 && (
              <ul>
                {menu?.slice(7).map((item, index) => (
                  <li key={index}>
                    <MyLink
                      link={item.link}
                      linkType={item.type}
                      text={item.label}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <path d="M9 6l6 6l-6 6" />
                      </svg>
                    </MyLink>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
      <div className="col-lg-4 col-lg-4 col-md-6 col-sm-12">
        <div className="footer-widget footer-nav-widget mb-40 wow fadeInDown">
          <h4 className="widget-title">Service Areas</h4>
          <div className="footer-widget-nav">
            <ul>
              {serviceAreas?.map((area, index) => (
                <li key={index} className="service-area-item">
                  <div
                    className="service-area-header"
                    onClick={() => toggleArea(index)}
                    role="button"
                    tabIndex={0}
                    aria-expanded={expandedArea === index}
                    aria-controls={`service-area-${index}`}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggleArea(index);
                      }
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    <span>{area.title}</span>
                    {area.links?.length > 0 &&
                      (expandedArea === index ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                          <path d="M6 15l6 -6l6 6" />
                        </svg>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                          <path d="M6 9l6 6l6 -6" />
                        </svg>
                      ))}
                  </div>
                  {area.links && (
                    <ul
                      className="footer-widget-nav service-area-links"
                      id={`service-area-${index}`}
                      style={{
                        display: expandedArea === index ? "block" : "none",
                        visibility:
                          expandedArea === index ? "visible" : "hidden",
                        height: expandedArea === index ? "auto" : "0",
                        overflow: "hidden",
                        transition: "all 0.3s ease",
                      }}
                      aria-hidden={expandedArea !== index}
                    >
                      {area.links.map((link, linkIndex) => (
                        <li key={linkIndex}>
                          <MyLink
                            link={link.link}
                            linkType={link.type}
                            text={link.label}
                            style={{
                              pointerEvents:
                                expandedArea === index ? "auto" : "none",
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden="true"
                            >
                              <path
                                stroke="none"
                                d="M0 0h24v24H0z"
                                fill="none"
                              />
                              <path d="M9 6l6 6l-6 6" />
                            </svg>
                          </MyLink>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
